import {
  AfterViewInit,
  OnDestroy,
  ElementRef,
  Input,
  Component,
  ViewChild,
  inject,
  HostListener
} from '@angular/core';
import { IPlugin, ModelService, PluginInjectorWidget } from 'core-client/api';
import { SharedModule } from '../../shared/shared.module';
import { VideoStreamService } from '../../shared/services/video-stream.service';
import { NgOptimizedImage } from '@angular/common';
import { distinctUntilChanged, filter, Subject, takeUntil } from 'rxjs';
import { IAhoyrtcVideoStreamWidget, StreamStatus } from '../../shared/interfaces/ahyortc-video-stream-widget.interface';

@Component({
    selector: 'lib-ahoyrtc-video-widget-view',
    imports: [SharedModule, NgOptimizedImage],
    templateUrl: './ahoyrtc-video-widget-view.component.html',
    styleUrl: './ahoyrtc-video-widget-view.component.scss'
})
export class AhoyrtcVideoWidgetViewComponent implements AfterViewInit, OnDestroy {
  @ViewChild('videoStreamContainer', { static: true }) videoStreamContainer: ElementRef | undefined;
  @ViewChild('videoPlayer',{ static: true }) videoElement: ElementRef | undefined;
  @Input() data: PluginInjectorWidget.IWidget | undefined;                 // Dynamic incoming widget data
  @Input() isHeaderHovered: boolean = false;          // Dynamic incoming widget data
  ahyortcVideoStreamWidget: IAhoyrtcVideoStreamWidget | undefined;
  widgetId: string | undefined;
  streamUuid: string | undefined;                     // Configured stream uuid
  streamStatus: StreamStatus = 'notAvailable';        // Track stream availability

  private apiKey: string | undefined;
  private apiUrl: string | undefined;

  private resizeObserver: ResizeObserver | undefined;
  private readonly ngUnsubscribe$: Subject<void> = new Subject<void>();

  protected readonly videoStreamService: VideoStreamService = inject(VideoStreamService);
  private readonly modelService: ModelService = inject(ModelService);

  async ngAfterViewInit(): Promise<void> {
    if (!this.data?.appPluginWidget) {
      console.error(`Widget data not correctly loaded.`);
      return;
    }

    this.ahyortcVideoStreamWidget = this.data.appPluginWidget;
    this.widgetId = this.ahyortcVideoStreamWidget._id;
    this.streamUuid = this.ahyortcVideoStreamWidget.settings?.streamUuid;

    const plugin = await this.modelService._plugin.getById(this.data.pluginId);

    if ('_id' in plugin) {
      const settings = plugin.settings;
      if (settings && plugin.enabled) {
        this.apiUrl = settings?.['url'] as string;
        this.apiKey = settings?.['api_key'] as string;

        if (this.apiKey.length > 0 && this.apiUrl.length > 0) {
          this.videoStreamService.setRequestHeader(this.apiKey, this.apiUrl);
        }
      }
    }

    this.observeStreamStatus(this.widgetId);

    // observe settings video stream changes for uuid
    this.listenOnWidgetSettingsChanges(this.widgetId);

    // observe plugin settings changes
    this.listenOnPluginSettingsChanges(this.data);

    if (this.streamUuid && this.apiKey && this.apiUrl) {
      await this.startStream(this.streamUuid, this.widgetId);
    }
    // this.observeResizeChanges();
  }

  async startStream(uuid: string, widgetId: string): Promise<void> {
    if (this.videoElement?.nativeElement) {
      await this.videoStreamService.handleStream(
          uuid,
          widgetId,
          this.videoElement.nativeElement
      );
    }
  }

  private observeStreamStatus(widgetId: string): void {
    this.videoStreamService.getStreamStatus$(widgetId)
    .pipe(
        distinctUntilChanged((prev: StreamStatus, curr: StreamStatus) => {
          // console.log(`Prev: ${prev}, Curr: ${curr}, widgetId: ${widgetId}`);
          return prev === curr;
        }),
        takeUntil(this.ngUnsubscribe$),
    )
    .subscribe((status: StreamStatus) => {
      this.streamStatus = status;
    });
  }

  private observeResizeChanges(): void {
    this.resizeObserver = new ResizeObserver(() => {
      this.setWidgetHeaderOnHover(this.isHeaderHovered);
    });

    if (this.videoStreamContainer?.nativeElement) {
      this.resizeObserver.observe(this.videoStreamContainer?.nativeElement)
    }
  }

  private setWidgetHeaderOnHover(isHeaderHovered: boolean): void {
    if (this.videoStreamContainer?.nativeElement) {
      this.videoStreamContainer.nativeElement.style.marginTop = isHeaderHovered ? '35px' : '0';
    }
  }

  private listenOnWidgetSettingsChanges(widgetId: string): void {
    this.videoStreamService.getStreamUuid$(widgetId)
    .pipe(
        takeUntil(this.ngUnsubscribe$),
        filter((uuid: string | undefined): uuid is string => uuid != undefined),
    )
    .subscribe(async (uuid: string) => {
      if (!this.widgetId) {
        console.error(`Widget data not correctly loaded.`);
        return;
      }

      if (this.streamUuid === uuid) {
        console.log('No video stream changes.');
        return;
      }

      this.streamUuid = uuid;

      // Clean connection for old uuid
      if (this.widgetId && this.streamUuid) {
        const connectionKey = `${this.widgetId}-${this.streamUuid}`;
        this.videoStreamService.cleanupConnection(connectionKey);
      }

      if (this.apiKey && this.apiUrl) {
        // Request stream for new uuid
        await this.startStream(uuid, this.widgetId);
      }
    });
  }

  private listenOnPluginSettingsChanges(widget: PluginInjectorWidget.IWidget): void {
    this.modelService._plugin.observePlugin$()
    .pipe(
        filter((data: { plugin: IPlugin, event: 'activate'|'update' }) =>
            data.plugin._id === widget?.pluginId
        ),
        takeUntil(this.ngUnsubscribe$)
    ).subscribe(async (data: { plugin: IPlugin, event: 'activate' | 'update' }) => {
      if (!this.widgetId) {
        console.error(`Widget data not correctly loaded.`);
        return;
      }

      let settingsChanged: boolean = false;
      if (data.plugin.enabled) {
        const settings = data.plugin.settings;
        const apiUrl = settings?.['url'] as string;
        const apiKey = settings?.['api_key'] as string;

        if (apiKey !== this.apiKey || apiUrl !== this.apiUrl) {
          settingsChanged = true;
          this.apiKey = apiKey;
          this.apiUrl = apiUrl;

          if (this.apiKey.length > 0 && apiUrl.length > 0) {
            this.videoStreamService.setRequestHeader(this.apiKey, this.apiUrl);
          }
        }

        if (settingsChanged && this.streamUuid && this.apiKey && this.apiUrl) {
          // Clean connection for old apikey or apiUrl
          const connectionKey = `${this.widgetId}-${this.streamUuid}`;
          this.videoStreamService.cleanupConnection(connectionKey);

          await this.startStream(this.streamUuid, this.widgetId);
        }
      } else {
        this.videoStreamService.onPause(this.videoElement?.nativeElement);
        if (this.streamStatus !== 'notAvailable') {
          this.videoStreamService.setStreamStatus(this.widgetId, 'notAvailable');

          // Clean connection for old apikey or apiUrl
          const connectionKey = `${this.widgetId}-${this.streamUuid}`;
          this.videoStreamService.cleanupConnection(connectionKey);
        }
      }
    })
  }

  @HostListener('window:beforeunload')
  async onBeforeReload(): Promise<void> { await this.ngOnDestroy(); }

  async ngOnDestroy(): Promise<void> {
    // Clean widget connection
    if (this.widgetId && this.streamUuid) {
      const connectionKey = `${this.widgetId}-${this.streamUuid}`;
      this.videoStreamService.cleanupConnection(connectionKey);
    }

    if (this.widgetId) {
      this.videoStreamService.removeStreamUUid(this.widgetId);
      this.videoStreamService.removeStreamStatus(this.widgetId);
    }

    // Clean subscriptions
    this.ngUnsubscribe$.complete();

    // Remove HostListener
    window.removeEventListener('window:beforeunload', this.onBeforeReload);

    // Clean up ResizeObserver
    this.resizeObserver?.disconnect();
  }
}
